'use client';

import * as React from 'react';

import { PARENT_WINDOW_MESSAGE_TYPE } from '../lib/embedHandshake';
import {
  type EmbedSize,
  initialEmbedHeightState,
  nextEmbedHeight,
} from '../lib/embedHeight';

const SAFETY_NET_INTERVAL_MS = 500;

const getEmbedSize = (): EmbedSize => {
  const { body, documentElement } = document;
  const { marginTop, marginBottom, borderTopWidth, borderBottomWidth } =
    window.getComputedStyle(body);
  const edges = [marginTop, marginBottom, borderTopWidth, borderBottomWidth]
    .map((size) => parseFloat(size) || 0)
    .reduce((sum, size) => sum + size, 0);

  return {
    height: Math.ceil(body.scrollHeight + edges),
    width: documentElement.clientWidth,
  };
};

const PostMessageComponent: React.FC = () => {
  const [parentWindowOrigin, setParentWindowOrigin] = React.useState('*');
  const [parentWindowId, setParentWindowId] = React.useState<string | null>(
    null,
  );

  React.useEffect(() => {
    const earlyReporter = window.narvarTrackEmbed;
    earlyReporter?.stop();
    if (earlyReporter?.parentWindowId && earlyReporter.parentWindowOrigin) {
      setParentWindowOrigin(earlyReporter.parentWindowOrigin);
      setParentWindowId(earlyReporter.parentWindowId);
    }

    const cb = ({ origin, data, source }: MessageEvent): void => {
      if (source !== window.parent) return;
      if (typeof data !== 'object' || !data) return;
      if (data.type !== PARENT_WINDOW_MESSAGE_TYPE) return;

      const { parentWindowId: newParentWindowId } = data.data ?? {};
      if (typeof newParentWindowId !== 'string' || !newParentWindowId) return;

      setParentWindowOrigin(origin);
      setParentWindowId(newParentWindowId);
    };

    window.addEventListener('message', cb);

    return (): void => {
      window.removeEventListener('message', cb);
    };
  }, []);

  const embedHeightRef = React.useRef(initialEmbedHeightState);

  React.useEffect(() => {
    if (!parentWindowOrigin || !parentWindowId) return undefined;

    let cancelled = false;

    const report = (force: boolean): void => {
      if (cancelled) return;

      const measured = getEmbedSize();

      if (!Number.isFinite(measured.height) || measured.height <= 0) return;

      const { state, height } = nextEmbedHeight(
        embedHeightRef.current,
        measured,
        performance.now(),
        force,
      );
      embedHeightRef.current = state;
      if (height === null) return;

      window.parent.postMessage(
        { contentDocumentSize: height, parentWindowId },
        parentWindowOrigin,
      );
    };

    const reportIfChanged = (): void => report(false);

    report(true);

    const intervalId = window.setInterval(
      reportIfChanged,
      SAFETY_NET_INTERVAL_MS,
    );

    if (document.readyState !== 'complete') {
      window.addEventListener('load', reportIfChanged);
    }
    window.addEventListener('resize', reportIfChanged);

    const reportOnVisible = (): void => {
      if (document.visibilityState === 'visible') report(true);
    };
    document.addEventListener('visibilitychange', reportOnVisible);

    const observer =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(reportIfChanged)
        : null;
    observer?.observe(document.body);
    observer?.observe(document.documentElement);

    return (): void => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('load', reportIfChanged);
      window.removeEventListener('resize', reportIfChanged);
      document.removeEventListener('visibilitychange', reportOnVisible);
      observer?.disconnect();
    };
  }, [parentWindowOrigin, parentWindowId]);

  return null;
};

export default PostMessageComponent;
